import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "../../config";
import InstructorStaffLayout from "../../components/InstructorStaffLayout";
import ConstrainedSelect from "../../components/ConstrainedSelect";
import StudentTrackingFilters from "../../components/StudentTrackingFilters";
import StudentTrackingPagination from "../../components/StudentTrackingPagination";
import { getMessage } from "../../data/locales";
import { getHomeRouteByRole, isInstructor } from "../../utils/roles";
import {
  collectStudentTrackingTopics,
  filterStudentTrackingList,
  getStudentTrackingTopicKey,
  hasActiveStudentTrackingFilters,
  paginateStudentTrackingList,
} from "../../utils/studentTrackingList";
import "./ProgresoAlumnos.css";

export function getProgressSidebarLinks(rol) {
  const homeLink = {
    path: getHomeRouteByRole(rol || "instructor"),
    label: getMessage("studentTracking.backToHome"),
    variant: "exit",
  };

  if (isInstructor(rol)) {
    return [
      { path: "/Registrousuario", label: "Registrar usuario / Invitación de pago" },
      { path: "/GestionCursos", label: "Gestionar cursos" },
      { path: "/GestionAnuncios", label: "Comunicados por curso" },
      { path: "/mensajeria-interna", label: "Mensajería corporativa" },
      homeLink,
    ];
  }

  return [
    { path: "/centro-control-director", label: "Administrar Usuarios" },
    { path: "/GestionCursos", label: "Gestionar Cursos" },
    { path: "/Autorizaciones", label: "Autorizaciones y acciones sensibles" },
    { path: "/GestionAnuncios", label: "Comunicados" },
    { path: "/Registro", label: "Registro de Actividades" },
    { path: "/mensajeria-interna", label: "Mensajería Corporativa" },
    { path: "/configuracion", label: "Configuración" },
    homeLink,
  ];
}

function courseOptionLabel(curso) {
  return curso.titulo || curso.nombre || String(curso._id);
}

function ProgressCourseSelect({ value, cursos, loading, onChange }) {
  return (
    <label className="pa-label pa-course-field" htmlFor="pa-course-select">
      <span>{getMessage("studentTracking.courseLabel")}</span>
      <ConstrainedSelect
        id="pa-course-select"
        className="pa-select"
        value={value}
        disabled={loading}
        ariaLabel={getMessage("studentTracking.courseLabel")}
        onChange={onChange}
        options={[
          {
            value: "",
            label: loading
              ? getMessage("studentTracking.courseLoading")
              : getMessage("studentTracking.coursePlaceholder"),
          },
          ...cursos.map((curso) => ({
            value: String(curso._id),
            label: courseOptionLabel(curso),
          })),
        ]}
      />
    </label>
  );
}

ProgressCourseSelect.propTypes = {
  value: PropTypes.string.isRequired,
  cursos: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    titulo: PropTypes.string,
    nombre: PropTypes.string,
  }).isRequired).isRequired,
  loading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

function getProgressPctClass(value) {
  const pct = Number(value) || 0;
  if (pct >= 80) return "alto";
  if (pct >= 50) return "medio";
  return "bajo";
}

function getTemaKey(tema) {
  return getStudentTrackingTopicKey(tema) || "tema";
}

function getHistorialKey(item) {
  return `${item?.fecha || "sin-fecha"}-${item?.nota ?? "sin-nota"}-${item?.tiempo ?? "sin-tiempo"}`;
}

function formatNumberOrDash(value, decimals) {
  return value == null ? "—" : Number(value).toFixed(decimals);
}

const ProgresoAlumnos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rol, setRol] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [cursos, setCursos] = useState([]);
  const [cursoSeleccionado, setCursoSeleccionado] = useState("");
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [cargandoCursos, setCargandoCursos] = useState(true);
  const [error, setError] = useState("");
  const [expandido, setExpandido] = useState(null);
  const [studentQuery, setStudentQuery] = useState("");
  const [topicKey, setTopicKey] = useState("");
  const [evaluatedContents, setEvaluatedContents] = useState(false);
  const [unevaluatedContents, setUnevaluatedContents] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok && data.usuario) setRol(data.usuario.rol || null);
        else setRol(null);
      })
      .catch((err) => {
        console.error("Token verification failed:", err);
        setRol(null);
      })
      .finally(() => setRoleLoaded(true));
  }, []);

  useEffect(() => {
    if (!roleLoaded || rol !== "director") return;
    if (location.pathname === "/instructor/progreso-alumnos") {
      navigate("/director/progreso-alumnos", { replace: true });
    }
  }, [roleLoaded, rol, location.pathname, navigate]);

  useEffect(() => {
    const cargarCursos = async () => {
      try {
        const res = await fetch(`${API_URL}/cursos`, { credentials: "include" });
        if (!res.ok) throw new Error("Error al cargar cursos");
        const list = await res.json();
        setCursos(Array.isArray(list) ? list : []);
      } catch {
        setError("No se pudieron cargar los cursos.");
      } finally {
        setCargandoCursos(false);
      }
    };
    cargarCursos();
  }, []);

  const cargarProgreso = async () => {
    if (!cursoSeleccionado) {
      setError(getMessage("studentTracking.selectCourseError"));
      return;
    }
    setError("");
    setCargando(true);
    setDatos(null);
    try {
      const res = await fetch(
        `${API_URL}/api/cursos/${cursoSeleccionado}/progreso-alumnos`,
        { credentials: "include" }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Error al cargar progreso");
      }
      const json = await res.json();
      setDatos(json);
      setStudentQuery("");
      setTopicKey("");
      setEvaluatedContents(false);
      setUnevaluatedContents(false);
      setPage(1);
      setExpandido(null);
    } catch (e) {
      setError(e.message || "Error al obtener el progreso.");
    } finally {
      setCargando(false);
    }
  };

  const trackingFilters = useMemo(() => ({
    query: studentQuery,
    topicKey,
    evaluatedContents,
    unevaluatedContents,
  }), [studentQuery, topicKey, evaluatedContents, unevaluatedContents]);

  const topicOptions = useMemo(
    () => collectStudentTrackingTopics(datos?.alumnos),
    [datos],
  );
  const filteredAlumnos = useMemo(
    () => filterStudentTrackingList(datos?.alumnos, trackingFilters),
    [datos, trackingFilters],
  );
  const pagedAlumnos = useMemo(
    () => paginateStudentTrackingList(filteredAlumnos, page),
    [filteredAlumnos, page],
  );
  const filtersActive = hasActiveStudentTrackingFilters(trackingFilters);

  const resetPageAndDetail = () => {
    setPage(1);
    setExpandido(null);
  };

  const sidebarLinks = roleLoaded ? getProgressSidebarLinks(rol) : [];

  return (
    <InstructorStaffLayout
      className="pa-layout"
      title={getMessage("studentTracking.title")}
      description={getMessage("studentTracking.description")}
      links={sidebarLinks}
    >
      <div className="pa-controls">
        <ProgressCourseSelect
          value={cursoSeleccionado}
          cursos={cursos}
          loading={cargandoCursos}
          onChange={(courseId) => {
            setCursoSeleccionado(courseId);
            setDatos(null);
            setStudentQuery("");
            setTopicKey("");
            setEvaluatedContents(false);
            setUnevaluatedContents(false);
            setPage(1);
            setExpandido(null);
          }}
        />
        <button
          type="button"
          className="pa-btn"
          onClick={cargarProgreso}
          disabled={cargando || !cursoSeleccionado}
        >
          {cargando
            ? getMessage("studentTracking.viewProgressLoading")
            : getMessage("studentTracking.viewProgress")}
        </button>
      </div>

      {error && <p className="pa-error">{error}</p>}

      {datos && (
        <div className="pa-resultado">
          <h2>{datos.titulo}</h2>
          <p className="pa-meta">
            {filtersActive
              ? getMessage("studentTracking.resultsCount", {
                filtered: filteredAlumnos.length,
                total: datos.alumnos?.length ?? 0,
              })
              : `${datos.alumnos?.length ?? 0} alumno(s)`}
            {" · "}
            {datos.totalTemas ?? 0} temas evaluables · {datos.totalContenidos ?? 0} contenidos
          </p>
          <StudentTrackingFilters
            query={studentQuery}
            topicKey={topicKey}
            topicOptions={topicOptions}
            evaluatedContents={evaluatedContents}
            unevaluatedContents={unevaluatedContents}
            canClear={filtersActive}
            onQueryChange={(value) => {
              setStudentQuery(value);
              resetPageAndDetail();
            }}
            onTopicChange={(value) => {
              setTopicKey(value);
              resetPageAndDetail();
            }}
            onEvaluatedContentsChange={(checked) => {
              setEvaluatedContents(checked);
              resetPageAndDetail();
            }}
            onUnevaluatedContentsChange={(checked) => {
              setUnevaluatedContents(checked);
              resetPageAndDetail();
            }}
            onClear={() => {
              setStudentQuery("");
              setTopicKey("");
              setEvaluatedContents(false);
              setUnevaluatedContents(false);
              resetPageAndDetail();
            }}
          />
          {filteredAlumnos.length === 0 ? (
            <p className="pa-empty-filters">{getMessage("studentTracking.noFilterResults")}</p>
          ) : (
          <div className="pa-table-wrap">
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Alumno</th>
                  <th>Email</th>
                  <th>% avance</th>
                  <th>% contenido</th>
                  <th>Nota media</th>
                  <th>Temas (dominados / total)</th>
                  <th>Contenido (visto / total)</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {pagedAlumnos.items.map((a) => (
                  <React.Fragment key={a.usuarioId}>
                    <tr>
                      <td data-label="Alumno">{a.nombre}</td>
                      <td data-label="Email">{a.email}</td>
                      <td data-label="% avance">
                        <span className={`pa-pct pa-pct-${getProgressPctClass(a.porcentajeAvance)}`}>
                          {a.porcentajeAvance}%
                        </span>
                      </td>
                      <td data-label="% contenido">
                        <span className={`pa-pct pa-pct-${getProgressPctClass(a.progresoContenido?.porcentajeContenido)}`}>
                          {a.progresoContenido?.porcentajeContenido ?? 0}%
                        </span>
                      </td>
                      <td data-label="Nota media">{formatNumberOrDash(a.notaMedia, 2)}</td>
                      <td data-label="Temas">{a.temasDominados} / {a.temasTotales}</td>
                      <td data-label="Contenido">
                        {a.progresoContenido?.contenidosVisualizados ?? 0} / {a.progresoContenido?.totalContenidos ?? 0}
                      </td>
                      <td data-label="Detalle">
                        <button
                          type="button"
                          className="pa-btn-small"
                          onClick={() => setExpandido(expandido === a.usuarioId ? null : a.usuarioId)}
                          aria-expanded={expandido === a.usuarioId}
                          aria-label={expandido === a.usuarioId ? "Ocultar detalle de seguimiento" : "Ver detalle de seguimiento"}
                        >
                          {expandido === a.usuarioId ? "Ocultar" : "Ver detalle"}
                        </button>
                      </td>
                    </tr>
                    {expandido === a.usuarioId && (
                      <tr className="pa-detail-row">
                        <td colSpan={8}>
                          <div className="pa-detail">
                            <h4>Contenido visualizado</h4>
                            {a.progresoContenido?.contenidos?.length > 0 ? (
                              <ul className="pa-contenido-list">
                                {a.progresoContenido.contenidos.map((contenido) => (
                                  <li
                                    key={contenido.key}
                                    className={`pa-contenido-item ${contenido.visto ? "pa-contenido-item--visto" : "pa-contenido-item--pendiente"}`}
                                  >
                                    <span className={`pa-estado ${contenido.visto ? "pa-estado-dominado" : "pa-estado-sin_intento"}`}>
                                      {contenido.visto ? "Visto" : "Pendiente"}
                                    </span>
                                    <span>
                                      {contenido.tipo === "archivo" ? "Material" : "Apartado"} · {contenido.ruta || contenido.titulo}
                                    </span>
                                    {contenido.vistoEn ? (
                                      <span className="pa-contenido-date">
                                        {new Date(contenido.vistoEn).toLocaleDateString()}
                                      </span>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p>No hay contenidos configurados para este curso.</p>
                            )}
                            <h4>Tests por tema</h4>
                            {a.resumenPorTema?.length > 0 ? (
                              <ul className="pa-tema-list">
                                {a.resumenPorTema.map((t) => (
                                  <li key={getTemaKey(t)} className="pa-tema-item">
                                    <div className="pa-tema-header">
                                      <strong>{t.bloque} → {t.tema}</strong>: {t.intentos} intento(s), mejor nota {formatNumberOrDash(t.mejorNota, 1)}, estado <span className={`pa-estado pa-estado-${t.estado}`}>{t.estado}</span>
                                    </div>
                                    {t.historial && t.historial.length > 0 && (
                                      <ul className="pa-historial-list">
                                        {t.historial.map((h) => (
                                          <li key={getHistorialKey(h)} className={`pa-historial-item pa-historial-${h.estado}`}>
                                            {h.fecha && new Date(h.fecha).toLocaleDateString()} · Nota: {Number.isFinite(Number(h.nota)) ? Number(h.nota).toFixed(1) : "—"} · {Number.isFinite(Number(h.tiempo)) ? `${h.tiempo}s` : "—"} · {h.estado === "dominado" ? "APTO" : "NO APTO"}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p>No hay datos de tests disponibles.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          )}
          <StudentTrackingPagination
            page={pagedAlumnos.page}
            totalPages={pagedAlumnos.totalPages}
            from={pagedAlumnos.from}
            to={pagedAlumnos.to}
            total={pagedAlumnos.total}
            onPrev={() => {
              setPage((current) => Math.max(1, current - 1));
              setExpandido(null);
            }}
            onNext={() => {
              setPage((current) => current + 1);
              setExpandido(null);
            }}
          />
        </div>
      )}
    </InstructorStaffLayout>
  );
};

export default ProgresoAlumnos;
